/**
 * Runtime configuration resolved at container start — never baked at build time.
 *
 * The frontend ships as one environment-agnostic static artifact (the same
 * image is promoted dev→prod), so per-environment values cannot be inlined at
 * `npm run build`. In built images, `docker-entrypoint.sh` writes
 * `/env-config.js`, which sets `window._env_` before the app bundle loads.
 * Locally (`npm start`) no entrypoint runs, so we fall back to the
 * `REACT_APP_*` values CRA inlines from the dev environment.
 *
 * Only non-secret values belong here: the Cognito hosted-UI domain, the
 * public app client id used to build the browser→Cognito authorize redirect,
 * and the Google Maps browser key and Map ID (the key is public by design and
 * restricted by HTTP referrer in Google Cloud). The confidential client secret
 * stays backend-side and is never exposed.
 */

interface RuntimeConfig {
  COGNITO_DOMAIN: string;
  USER_POOL_CLIENT_ID: string;
  GOOGLE_MAPS_BROWSER_KEY: string;
  GOOGLE_MAPS_MAP_ID: string;
}

declare global {
  interface Window {
    _env_?: Partial<RuntimeConfig>;
  }
}

// Each getter passes its `process.env.REACT_APP_*` fallback as a literal
// reference, because CRA inlines those at build time and a dynamic
// `process.env[key]` lookup would not be replaced. Empty counts as absent.
function readRuntimeValue(key: keyof RuntimeConfig, buildTimeFallback: string | undefined): string {
  return window._env_?.[key] || buildTimeFallback || '';
}

/** Cognito hosted-UI domain (full hostname), e.g. `x.auth.us-west-2.amazoncognito.com`. */
export function getCognitoDomain(): string {
  return readRuntimeValue('COGNITO_DOMAIN', process.env.REACT_APP_COGNITO_DOMAIN);
}

/** Public Cognito app client id used in the authorize redirect. */
export function getUserPoolClientId(): string {
  return readRuntimeValue('USER_POOL_CLIENT_ID', process.env.REACT_APP_USER_POOL_CLIENT_ID);
}

/** Google Maps browser key (public, referrer-restricted); empty when maps are not configured. */
export function getGoogleMapsBrowserKey(): string {
  return readRuntimeValue('GOOGLE_MAPS_BROWSER_KEY', process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY);
}

/** Google Maps cloud-styled Map ID; empty when maps are not configured. */
export function getGoogleMapsMapId(): string {
  return readRuntimeValue('GOOGLE_MAPS_MAP_ID', process.env.REACT_APP_GOOGLE_MAPS_MAP_ID);
}
